import React, { useEffect, useState } from 'react';
import { createRecord } from '../api';
import { ServiceRecord, ServiceRecordCreateDto } from '../types';
import { parsePLNToCents } from '../utils/formatters';

interface NewRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRecordCreated: (newRecord: ServiceRecord) => void;
}

const DEVICE_TYPES = ['Laptop', 'Smartfon', 'Komputer PC', 'Tablet', 'Inne'];

export const NewRecordModal: React.FC<NewRecordModalProps> = ({
  isOpen,
  onClose,
  onRecordCreated,
}) => {
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [deviceType, setDeviceType] = useState('Laptop');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [visualCondition, setVisualCondition] = useState('');
  const [pricePln, setPricePln] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setClientName('');
      setClientPhone('');
      setDeviceType('Laptop');
      setBrand('');
      setModel('');
      setSerialNumber('');
      setDescription('');
      setVisualCondition('');
      setPricePln('');
      setNotes('');
      setErrorMessage(null);
      setIsSubmitting(false);
      setIdempotencyKey(crypto.randomUUID());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedPhone = clientPhone.trim();
    if (!trimmedPhone) {
      setErrorMessage('Numer telefonu klienta jest wymagany.');
      return;
    }

    const trimmedDesc = description.trim();
    if (!trimmedDesc) {
      setErrorMessage('Opis usterki / zgłoszenia jest wymagany.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const priceCents = pricePln.trim() ? parsePLNToCents(pricePln) : 0;

    const createDto: ServiceRecordCreateDto = {
      clientName: clientName.trim() || undefined,
      clientPhone: trimmedPhone,
      deviceType: deviceType || 'Laptop',
      brand: brand.trim() || undefined,
      model: model.trim() || undefined,
      serialNumber: serialNumber.trim() || undefined,
      description: trimmedDesc,
      visualCondition: visualCondition.trim() || undefined,
      status: 'RECEIVED',
      priceCents,
      notes: notes.trim() || undefined,
      idempotencyKey,
    };

    try {
      const createdRecord = await createRecord(createDto, idempotencyKey);
      setIsSubmitting(false);
      onRecordCreated(createdRecord);
      onClose();
    } catch (err: any) {
      setIsSubmitting(false);
      if (err.status === 400) {
        setErrorMessage(err.message ? `Sprawdź dane formularza: ${err.message}` : 'Sprawdź dane formularza.');
      } else if (err.status === 401 || err.status === 403) {
        setErrorMessage('Brak dostępu do Central CRM.');
      } else if (err.status === 409) {
        setErrorMessage('Nie udało się przydzielić numeru zgłoszenia. Spróbuj ponownie.');
      } else {
        setErrorMessage('Nie udało się utworzyć zgłoszenia. Spróbuj ponownie.');
      }
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        className="modal-card"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '680px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.25rem' }}>➕</span>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
              Nowe Zgłoszenie Serwisowe
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-icon-subtle"
            onClick={onClose}
            disabled={isSubmitting}
            style={{ fontSize: '1.1rem', padding: '2px 8px' }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 130px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {errorMessage && (
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#991b1b',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                ⛔ {errorMessage}
              </div>
            )}

            {/* SEKCJA 1: Dane klienta */}
            <div style={{ backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                👤 DANE KLIENTA
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="field-group">
                  <label className="field-label">Imię i Nazwisko / Firma</label>
                  <input
                    type="text"
                    className="field-input"
                    placeholder="np. Jan Kowalski"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">
                    Telefon Kontaktowy <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    className="field-input"
                    placeholder="np. +48 600 111 222"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* SEKCJA 2: Urządzenie */}
            <div style={{ backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                💻 URZĄDZENIE I SPRZĘT
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="field-group">
                  <label className="field-label">Typ Urządzenia</label>
                  <select
                    className="field-input"
                    value={deviceType}
                    onChange={(e) => setDeviceType(e.target.value)}
                    disabled={isSubmitting}
                  >
                    {DEVICE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label">Marka</label>
                  <input
                    type="text"
                    className="field-input"
                    placeholder="np. Dell, Asus, Apple"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Model</label>
                  <input
                    type="text"
                    className="field-input"
                    placeholder="np. Latitude 5520"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Numer Seryjny (SN)</label>
                  <input
                    type="text"
                    className="field-input"
                    placeholder="np. SN12345678"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            {/* SEKCJA 3: Opis zgłoszenia i stan */}
            <div style={{ backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                📋 OPIS ZGŁOSZENIA I STAN
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div className="field-group">
                  <label className="field-label">
                    Opis Usterki <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    className="field-input"
                    placeholder="Szczegółowy opis zgłaszanej usterki / problemu..."
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    disabled={isSubmitting}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Stan Wizualny / Uwagi Przyjęcia</label>
                  <textarea
                    className="field-input"
                    placeholder="np. Drobne rysy na obudowie, brak zasilacza..."
                    rows={2}
                    value={visualCondition}
                    onChange={(e) => setVisualCondition(e.target.value)}
                    disabled={isSubmitting}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>

            {/* SEKCJA 4: Rozliczenie i Notatka */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ backgroundColor: '#f8fafc', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#475569', marginBottom: '10px' }}>
                  🛠️ ROZLICZENIE
                </div>
                <div className="field-group">
                  <label className="field-label">Szacowana Cena (PLN)</label>
                  <input
                    type="text"
                    className="field-input"
                    placeholder="np. 200 lub 0,00"
                    value={pricePln}
                    onChange={(e) => setPricePln(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <small style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                    Domyślny status: <strong>Przyjęte (RECEIVED)</strong>
                  </small>
                </div>
              </div>

              <div style={{ backgroundColor: '#fefce8', padding: '12px 14px', borderRadius: '8px', border: '1px solid #fef08a' }}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#854d0e', marginBottom: '10px' }}>
                  📌 NOTATKA WEWNĘTRZNA
                </div>
                <div className="field-group">
                  <label className="field-label" style={{ color: '#854d0e' }}>Notatka Serwisu</label>
                  <textarea
                    className="field-input"
                    placeholder="Uwagi niewidoczne dla klienta..."
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isSubmitting}
                    style={{ resize: 'vertical', backgroundColor: '#ffffff' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '14px 20px',
              borderTop: '1px solid var(--border-light, #e2e8f0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              backgroundColor: '#f8fafc',
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
              style={{ padding: '8px 18px', fontWeight: 600 }}
            >
              {isSubmitting ? 'Tworzenie zgłoszenia...' : '➕ Utwórz zgłoszenie'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
